import { useState } from "react";
import type { KeyedMutator } from "swr";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSpecValuesAvailableForMask } from "@/hooks/useSpecLists";
import { RawSpecList, SpecList, SpecListVersion } from "@/types/specList";
import { cn } from "@/lib/utils";
import { ModalContentError, ModalContentLoading } from "@/components/shared/Modal";
import { UpdateMethod } from "./types";
import { UpdateMethodSelector } from "./UpdateMethodSelector";
import { UploadNewVersionForm } from "./UploadNewVersionForm";
import { NewSpecListVersionForm } from "./NewSpecListVersionForm";

interface CreateNewSpecListVersionDialogProps {
  currentVersion: SpecListVersion;
  maskId?: string;
  masterVersion?: SpecListVersion;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  refetch: KeyedMutator<RawSpecList>;
  specList: SpecList;
}

export function NewSpecListVersionDialog({
  currentVersion,
  maskId,
  masterVersion,
  onOpenChange,
  open,
  refetch,
  specList,
}: CreateNewSpecListVersionDialogProps) {
  const { error, isLoading, specValues } = useSpecValuesAvailableForMask(masterVersion?.id, maskId);
  const [showMasterSpecs, setShowMasterSpecs] = useState(false);

  const isMasterSpec = specList.type === "master";
  const [updateMethod, setUpdateMethod] = useState<UpdateMethod | undefined>(
    isMasterSpec ? undefined : "manual",
  );

  if (error) {
    return (
      <ModalContentError
        onOpenChange={onOpenChange}
        className="md:max-w-[80%] lg:w-[90%] lg:max-w-[1400px] mx-auto"
        description={error.message}
      />
    );
  }

  if (isLoading && updateMethod === "manual") {
    return (
      <ModalContentLoading
        className="md:max-w-[80%] lg:w-[90%] lg:max-w-[1400px] mx-auto"
        title="Create New Spec List Version"
        onOpenChange={onOpenChange}
      />
    );
  }

  const _specValues = specValues.length > 0 ? specValues : (masterVersion?.specValues ?? []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        className={cn(
          "mx-auto",
          updateMethod === "manual" ? "md:max-w-[80%] lg:w-[90%] lg:max-w-350" : "sm:max-w-lg",
        )}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>Create New Spec List Version</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Create a new Spec List Version based on "{specList.name} - V{currentVersion.version}"
        </DialogDescription>
        {updateMethod === undefined && (
          <UpdateMethodSelector onSelect={setUpdateMethod} onCancel={() => onOpenChange(false)} />
        )}
        {updateMethod === "upload" && (
          <UploadNewVersionForm
            specList={specList}
            onOpenChange={onOpenChange}
            onBack={() => setUpdateMethod(undefined)}
            refetch={refetch}
          />
        )}
        {updateMethod === "manual" && (
          <div className="flex-1 overflow-hidden">
            <NewSpecListVersionForm
              key={`${showMasterSpecs ? "with" : "without"}-master-specs`}
              currentVersion={currentVersion}
              masterVersionSpecs={_specValues}
              onBack={isMasterSpec ? () => setUpdateMethod(undefined) : undefined}
              onOpenChange={onOpenChange}
              refetch={refetch}
              setShowMasterSpecs={setShowMasterSpecs}
              showMasterSpecs={showMasterSpecs}
              specList={specList}
              specValues={currentVersion.specValues}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
